/* GYN form has three sections:
 1. Insurance Info
 2. Gyn Sign On Form
 3. Consent Form
*/

import { Box, Flex } from '@chakra-ui/react'

import {
  FieldMapValue,
  getGynIntroductionForm,
  User,
  USERS,
  ValidationErrors,
  WithId,
} from '@hb/shared'
import { useCallback, useMemo, useRef } from 'react'
import { useResizeObserver } from '../../hooks'
import { Loading } from '../Loading'
import { FormWizardTabsView } from '../forms/FormWizard/FormWizardTabsView'
import { renderStageWithInsuranceInfo } from '../forms/renderStage/renderStageWithInsuranceInfo'
import { GynIntakeCodeButton } from '../Users/Profile/Gyn/GynIntakeCodeButton'
import { GynMidwifeSelect } from './GynMidwifeSelect'
import { useGynCareView } from './context'
import { toGynFormData } from './utils'

export const GynFormWizard = ({
  onSubmit,
  admin,
  patient,
  headerRightItem,
}: {
  onSubmit: ((submitted: FieldMapValue) => Promise<ValidationErrors>) | null
  admin: boolean
  patient: WithId<User>
  headerRightItem?: React.ReactNode
}) => {
  const form = useMemo(
    () => getGynIntroductionForm(false, patient.insuranceInfo?.data),
    [patient.insuranceInfo?.data],
  )

  const formData = useMemo(() => toGynFormData('data', patient), [patient])
  const formCorrections = useMemo(() => toGynFormData('corrections', patient), [patient])
  const getStoragePath = useCallback(
    (stageId: string) => {
      switch (stageId) {
        case 'patient':
          return `${USERS}/${patient.id}`
        case 'insuranceInfo':
          return `${USERS}/${patient.id}/insuranceInfo/data`
        case 'history':
          return `${USERS}/${patient.id}/gyn/data/history`
        default:
          return null
      }
    },
    [patient],
  )
  return (
    <FormWizardTabsView
      renderStage={renderStageWithInsuranceInfo}
      withPreview
      adminView={admin}
      mobileViewBreakpoint={1250}
      patientId={patient.id}
      assessmentId={null}
      form={form}
      readOnly={!onSubmit}
      getStoragePath={getStoragePath}
      data={formData}
      corrections={formCorrections}
      onSubmit={onSubmit}
      headerRightItem={headerRightItem}
    />
  )
}

const GynDetailsForm = () => {
  const { patient, practiceId, onSubmit, admin } = useGynCareView()

  const bodyRef = useRef<HTMLDivElement>(null)
  const { width } = useResizeObserver(bodyRef, 'content')
  if (!practiceId) return <GynMidwifeSelect mobileLayout={width < 500} />
  return (
    <Flex flexFlow="column" ref={bodyRef} w="100%" h="100%" px={1} gap={2}>
      <GynMidwifeSelect mobileLayout={width < 500} />
      <Box flex={1} minH="0">
        <GynFormWizard
          admin={admin}
          patient={patient}
          onSubmit={onSubmit}
          headerRightItem={admin ? <GynIntakeCodeButton /> : undefined}
        />
      </Box>
    </Flex>
  )
}

export const GynIntakeForm = ({ user }: { user: WithId<User> | null }) => (
  <Box flexFlow="column" w="100%" h="100%">
    {user ? <GynDetailsForm /> : <Loading text="Loading your information..." />}
  </Box>
)
